import { Component } from "@theme/component";

/**
 * @typedef {Object} ReviewCardRefs
 * @property {HTMLElement} [defaultContent]
 * @property {HTMLElement} [hoverContent]
 * @property {HTMLVideoElement} [videoPreview]
 */

/**
 * Review card component with hover states and video modal integration.
 *
 * @extends {Component<ReviewCardRefs>}
 */
class NcnReviewCard extends Component {
  #prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener("mouseenter", this.#handleMouseEnter);
    this.addEventListener("mouseleave", this.#handleMouseLeave);
    this.addEventListener("focus", this.#handleFocus);
    this.addEventListener("blur", this.#handleBlur);

    // Add click handler for video modal (if card has video)
    if (this.dataset.hasVideo === "true") {
      this.addEventListener("click", this.#handleClick);
      this.addEventListener("keydown", this.#handleKeydown);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener("mouseenter", this.#handleMouseEnter);
    this.removeEventListener("mouseleave", this.#handleMouseLeave);
    this.removeEventListener("focus", this.#handleFocus);
    this.removeEventListener("blur", this.#handleBlur);
    this.removeEventListener("click", this.#handleClick);
    this.removeEventListener("keydown", this.#handleKeydown);
  }

  #handleMouseEnter = () => {
    this.classList.add("ncn-review-card--active");
    this.#playVideoPreview();
  };

  #handleMouseLeave = () => {
    this.classList.remove("ncn-review-card--active");
    this.#pauseVideoPreview();
  };

  #handleFocus = () => this.classList.add("ncn-review-card--active");
  #handleBlur = () => this.classList.remove("ncn-review-card--active");

  /**
   * Opens video modal when card is clicked
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    event.preventDefault();
    this.#openVideoModal();
  };

  /**
   * Opens video modal when Enter or Space is pressed
   * @param {KeyboardEvent} event
   */
  #handleKeydown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      this.#openVideoModal();
    }
  };

  /**
   * Opens the video modal using native dialog API
   */
  #openVideoModal() {
    const modalId = this.dataset.modalId;
    if (!modalId) return;

    /** @type {HTMLDialogElement | null} */
    const modal = document.getElementById(modalId);
    if (!(modal instanceof HTMLDialogElement)) return;

    // Show modal (handles focus trapping, ESC key, backdrop automatically)
    modal.showModal();

    // Load iframe src lazily when modal opens (performance + iOS compatibility)
    const iframe = modal.querySelector("iframe[data-video-src]");
    if (iframe instanceof HTMLIFrameElement && !iframe.src) {
      iframe.src = iframe.dataset.videoSrc || "";
    }

    // Handle deferred-media (video inside template tag)
    // State is reset on close, so loadContent() always needs to run
    /** @type {HTMLElement & { loadContent?: (focus?: boolean) => void } | null} */
    const deferredMedia = modal.querySelector("deferred-media");
    if (deferredMedia && !deferredMedia.hasAttribute("data-media-loaded")) {
      deferredMedia.loadContent?.(false);
    }

    // Now query for video - works for both:
    // 1. Direct video elements (already in DOM)
    // 2. Deferred-media videos (just added by loadContent above)
    const video = modal.querySelector("video");
    if (video instanceof HTMLVideoElement) {
      // Add playsinline for Safari/iOS (prevents fullscreen takeover)
      video.setAttribute("playsinline", "");
      video.playsInline = true;

      // Unmute the video (it's muted by default from video.liquid)
      video.muted = false;

      // Play the video - user interaction chain allows unmuted playback
      if (video.readyState >= HTMLMediaElement.HAVE_ENOUGH_DATA) {
        video.play().catch(() => {});
      } else {
        video.addEventListener("canplay", () => video.play().catch(() => {}), { once: true });
      }
    }

    // Store reference for focus restoration
    const triggerElement = this;

    // Handle close button click
    const closeBtn = modal.querySelector(".ncn-review-video-modal__close");
    closeBtn?.addEventListener(
      "click",
      () => this.#closeVideoModal(),
      { once: true }
    );

    // Handle backdrop click (clicking on dialog itself, not content)
    modal.addEventListener(
      "click",
      (event) => {
        if (event.target === modal) {
          this.#closeVideoModal();
        }
      },
      { once: true }
    );

    // Handle close event (triggered by ESC key or close() call)
    modal.addEventListener(
      "close",
      () => {
        this.#onModalClose(modal);
        triggerElement.focus();
      },
      { once: true }
    );
  }

  /**
   * Closes the video modal
   */
  #closeVideoModal() {
    const modalId = this.dataset.modalId;
    if (!modalId) return;

    /** @type {HTMLDialogElement | null} */
    const modal = document.getElementById(modalId);
    if (!(modal instanceof HTMLDialogElement)) return;

    modal.close();
  }

  /**
   * Handles modal close - resets video state completely
   * @param {HTMLDialogElement} modal
   */
  #onModalClose(modal) {
    // Stop external video (YouTube/Vimeo) by removing iframe src
    // This is more reliable than postMessage which has cross-origin issues
    const iframe = modal.querySelector("iframe[data-video-src]");
    if (iframe instanceof HTMLIFrameElement && iframe.src) {
      // Clear src to stop playback completely
      // The src will be restored from data-video-src when modal reopens
      iframe.src = "";
    }

    // Reset deferred-media to initial state for Shopify-hosted videos
    const deferredMedia = modal.querySelector("deferred-media");
    if (deferredMedia) {
      // Remove the cloned video element (it will be re-cloned from template on next open)
      const video = deferredMedia.querySelector("video");
      if (video) {
        video.pause();
        video.remove();
      }

      // Reset deferred-media attributes so loadContent() will run again
      deferredMedia.removeAttribute("data-media-loaded");

      // Reset poster button to visible state
      const posterButton = deferredMedia.querySelector(".deferred-media__poster-button");
      posterButton?.classList.remove("deferred-media__playing");
    }
  }

  #playVideoPreview() {
    if (this.#prefersReducedMotion.matches) return;
    const { videoPreview } = this.refs;
    if (videoPreview) {
      videoPreview.play().catch(() => {});
    }
  }

  #pauseVideoPreview() {
    const { videoPreview } = this.refs;
    if (videoPreview) {
      videoPreview.pause();
      videoPreview.currentTime = 0;
    }
  }
}

if (!customElements.get("ncn-review-card")) {
  customElements.define("ncn-review-card", NcnReviewCard);
}
